import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Button, Alert, Form, Badge, FilterGrid, FilterField, SortableTh, EmptyValue, ServiceAreaPill } from '@/components/ui';
import reportService, { ReportHistory } from '../../../services/reportService';
import ServerPagination from '../../../components/ServerPagination';
import FiltersCard from '../../../components/FiltersCard';
import { usePersistedListState } from '../../../hooks/usePersistedListState';
import './ReportHistoryView.css';
import { formatDateTime } from '@/utils/displayDateTime';

interface ReportHistoryViewProps {
  canCreateD2: boolean;
  canCreateD3: boolean;
}

interface ServiceArea {
  id: number;
  name: string;
  code: string;
}

const REPORT_TYPE_LABELS: Record<string, string> = {
  weekly_payroll: 'Payroll',
  weekly_overtime: 'Overtime',
  payroll_variance: 'Payroll Variance'
};

interface ExportHistoryFilters {
  type: string;
  serviceArea: string;
  exportType: string;
  startDate: string;
  endDate: string;
}

const DEFAULT_FILTERS: ExportHistoryFilters = {
  type: '',
  serviceArea: '',
  exportType: '',
  startDate: '',
  endDate: ''
};

const FILTER_KEYS = Object.keys(DEFAULT_FILTERS) as (keyof ExportHistoryFilters)[];

/** sessionStorage page key for the remembered list state (V2-024, FLT-09). */
const PAGE_KEY = 'exportHistory';

const getReportTypeLabel = (type: string): string =>
  REPORT_TYPE_LABELS[type] ??
  type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const ReportHistoryView: React.FC<ReportHistoryViewProps> = ({ canCreateD2, canCreateD3 }) => {
  const [history, setHistory] = useState<ReportHistory[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  
  // V2-026: the shared filter panel - the fields edit pending filters, Apply applies them.
  // Applied filters and the page are kept for the sign-in (V2-024).
  const [filters, setFilters] = usePersistedListState<ExportHistoryFilters>(PAGE_KEY, 'filters', DEFAULT_FILTERS);
  const [pendingFilters, setPendingFilters] = useState<ExportHistoryFilters>(filters);
  const [savedPage, setSavedPage] = usePersistedListState(PAGE_KEY, 'page', { page: 1, limit: 20 });

  // Pagination states
  const [pagination, setPagination] = useState({
    page: savedPage.page,
    limit: savedPage.limit,
    total: 0,
    totalPages: 0
  });

  useEffect(() => {
    setSavedPage({ page: pagination.page, limit: pagination.limit });
  }, [pagination.page, pagination.limit, setSavedPage]);

  // Fetch service areas on component mount
  useEffect(() => {
    const fetchServiceAreas = async () => {
      try {
        const areas = await reportService.getServiceAreas();
        setServiceAreas(areas);
      } catch (err) {
        console.error('Failed to fetch service areas:', err);
      }
    };
    
    fetchServiceAreas();
  }, []);

  const fetchHistory = async (page: number = 1, pageSize?: number): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await reportService.getReportHistory(
        filters,
        page,
        pageSize || pagination.limit
      );
      
      setHistory(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to load report history. Please try again later.');
      setHistory([]);
      setPagination({ page: 1, limit: 20, total: 0, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  };

  // The first load reopens the remembered page; a change of filters starts again at page 1.
  const firstLoadRef = useRef(true);
  useEffect(() => {
    fetchHistory(firstLoadRef.current ? savedPage.page : 1);
    firstLoadRef.current = false;
    // eslint-disable-next-line
  }, [filters]);

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
    fetchHistory(page);
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPagination(prev => ({ ...prev, limit: pageSize, page: 1 }));
    fetchHistory(1, pageSize);
  };

  const handleFilterChange = (key: keyof ExportHistoryFilters, value: string) => {
    setPendingFilters(prev => ({ ...prev, [key]: value }));
  };

  /** V2-076 / FLT-05: every applied filter that hides records (none hide anything by default). */
  const appliedFilterCount = FILTER_KEYS.filter((key) => filters[key] !== '').length;
  const hasActiveFilters = appliedFilterCount > 0;
  const hasUnappliedChanges = FILTER_KEYS.some((key) => pendingFilters[key] !== filters[key]);

  const applyFilters = () => {
    setFilters({ ...pendingFilters });
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setPendingFilters(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDownload = async (reportId: number): Promise<void> => {
    try {
      await reportService.downloadReportById(reportId);
    } catch (err) {
      setError('Failed to download report. Please try again later.');
    }
  };

  const formatDate = (createdAt: string | null): string => {
    if (createdAt !== null && createdAt !== undefined && createdAt !== '') {
      try {
        // Ensure we have a valid createdAt value
        const createdAtValue: string = createdAt as string;
        
        // Try parsing as ISO string first
        let date = new Date(createdAtValue);
        
        // If that fails, try parsing as timestamp if it's a number
        if (isNaN(date.getTime()) && !isNaN(Number(createdAtValue))) {
          date = new Date(Number(createdAtValue));
        }
        
        // If still invalid, try alternative parsing
        if (isNaN(date.getTime())) {
          // Handle potential date string variations
          const dateStr = createdAtValue.toString();
          // Try with a more explicit parse for common formats
          date = new Date(dateStr.replace(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/, '$1-$2-$3T$4:$5:$6Z'));
        }
        
        return isNaN(date.getTime()) ? '–' : formatDateTime(date);
      } catch (error) {
        console.warn('Date parsing error. Date value:', createdAt, 'Error:', error);
        return 'Invalid Date';
      }
    }
    return 'No Date';
  };

  const getReportTypeBadge = (type: string) => {
    const typeColors: { [key: string]: string } = {
      'd3': 'primary',
      'd2': 'info',
      'wildlife': 'success',
      'rockfall': 'warning',
      'signs': 'secondary',
      'culverts': 'dark',
      'cattleguard': 'danger',
      'equipment_usage': 'purple',
      'material_usage': 'info',
      'finance': 'success',
      'weekly_payroll': 'primary',
      'weekly_overtime': 'primary',
      'payroll_variance': 'primary'
    };
    
    return (
      <Badge bg={typeColors[type] || 'secondary'}>
        {getReportTypeLabel(type)}
      </Badge>
    );
  };

  const getExportTypeBadge = (exportType?: string) => {
    if (!exportType) return null;
    
    const isPreliminary = exportType.toLowerCase() === 'preliminary';
    return (
      <Badge bg={isPreliminary ? 'warning' : 'success'}>
        {exportType.charAt(0).toUpperCase() + exportType.slice(1).toLowerCase()}
      </Badge>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* No header Clear Filters button: Clear All lives in the shared filter panel (V2-026). */}
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <h2>Export History</h2>
      </div>

      <FiltersCard
        title="Filters"
        collapsible
        // FiltersCard starts collapsed on a phone and a tablet by itself (W-iP6).
        defaultCollapsed={false}
        onApply={applyFilters}
        applyDisabled={!hasUnappliedChanges}
        onClear={clearFilters}
        hasActiveFilters={hasActiveFilters}
        appliedFilterCount={appliedFilterCount}
      >
        <FilterGrid>
          <FilterField>
            <Form.Label htmlFor="export-history-type">Report Type</Form.Label>
            <Form.Select
              id="export-history-type"
              value={pendingFilters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="">All Types</option>
              <option value="d3">D3</option>
              <option value="d2">D2</option>
              <option value="wildlife">Wildlife</option>
              <option value="rockfall">Rockfall</option>
              <option value="signs">Signs</option>
              <option value="culverts">Culverts</option>
              <option value="cattleguard">Cattleguard</option>
              <option value="equipment_usage">Equipment Usage</option>
              <option value="material_usage">Material Usage</option>
              <option value="finance">Finance</option>
              <option value="weekly_payroll">Payroll</option>
              <option value="weekly_overtime">Overtime</option>
              <option value="payroll_variance">Payroll Variance</option>
            </Form.Select>
          </FilterField>
          <FilterField>
            <Form.Label htmlFor="export-history-export-type">Export Type</Form.Label>
            <Form.Select
              id="export-history-export-type"
              value={pendingFilters.exportType}
              onChange={(e) => handleFilterChange('exportType', e.target.value)}
            >
              <option value="">All Export Types</option>
              <option value="PRELIMINARY">Preliminary</option>
              <option value="FINAL">Final</option>
            </Form.Select>
          </FilterField>
          <FilterField>
            <Form.Label htmlFor="export-history-service-area">Service Area</Form.Label>
            <Form.Select
              id="export-history-service-area"
              value={pendingFilters.serviceArea}
              onChange={(e) => handleFilterChange('serviceArea', e.target.value)}
            >
              <option value="">All Service Areas</option>
              {/* Only show service areas that have a code */}
              {serviceAreas.filter(area => area.code).map((area) => (
                <option key={area.id} value={area.code}>
                  {area.code}
                </option>
              ))}
            </Form.Select>
          </FilterField>
          <FilterField>
            <Form.Label htmlFor="export-history-start-date">Start Date</Form.Label>
            <Form.Control
              id="export-history-start-date"
              type="date"
              value={pendingFilters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </FilterField>
          <FilterField>
            <Form.Label htmlFor="export-history-end-date">End Date</Form.Label>
            <Form.Control
              id="export-history-end-date"
              type="date"
              value={pendingFilters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </FilterField>
        </FilterGrid>
      </FiltersCard>

      {/* Results Summary */}
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
        <div className="tw-text-content-muted tw-text-sm results-summary">
          {pagination.total > 0 ? (
            `Showing ${((pagination.page - 1) * pagination.limit) + 1} to ${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total} reports`
          ) : (
            'No reports found'
          )}
        </div>
      </div>

      {/* Report History Table */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
        <Card style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
          <Card.Body className="tw-p-0" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
            {error && <Alert variant="danger" className="tw-m-3">{error}</Alert>}
            {loading ? (
              <div className="tw-text-center tw-py-4">Loading report history...</div>
            ) : (
              <>
                {/* This div is the single scroll region for the table (both axes) - it also has
                    to stay because it's what keeps the pagination footer below pinned outside the
                    scrolling area. Table's own `responsive` prop would add a second, nested
                    overflow-x-auto wrapper here; that's the "horizontal scroll only appears at
                    the bottom of the page" bug (two scroll containers of mismatched height), so
                    the table renders bare and this div is the only scroll mechanism. */}
                {/* V2-028: the report pane is the one vertical scroll area; this box only scrolls
                    sideways. It used to be a second vertical scroller about three rows tall. */}
                <div style={{ overflowX: 'auto', minWidth: 0 }}>
                  <Table className="tw-mb-0 report-history-table">
                    <thead>
                      <tr>
                        <SortableTh label="Date" />
                        <SortableTh label="Type" />
                        <SortableTh label="Export Type" />
                        <SortableTh label="File Name" />
                        <SortableTh label="Service Area" />
                        <SortableTh label="Generated By" />
                        <SortableTh label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((report: ReportHistory) => (
                        <tr key={report.id}>
                          <td>
                            {formatDate(report.createdAt)}
                          </td>
                          <td>
                            {getReportTypeBadge(report.type)}
                          </td>
                          <td>
                            {getExportTypeBadge(report.exportType)}
                          </td>
                          <td>{report.name || <EmptyValue />}</td>
                          <td><ServiceAreaPill value={report.serviceArea ?? null} compact /></td>
                          <td>{report.generatedBy?.name || 'Unknown'}</td>
                          <td>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => handleDownload(report.id)}
                            >
                              Download
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {history.length === 0 && (
                        <tr>
                          <td colSpan={7} className="tw-text-center tw-py-4">
                            No reports found matching the current filters
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
                
                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="tw-p-3 tw-border-t tw-border-line pagination-container">
                    <ServerPagination
                      currentPage={pagination.page}
                      totalPages={pagination.totalPages}
                      totalCount={pagination.total}
                      pageSize={pagination.limit}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                      loading={loading}
                    />
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default ReportHistoryView; 